import useSWR, { SWRResponse } from "swr";

import { useAuth, useSWRApiErrorHandler } from "../common/hooks";
import { InvoiceFormValues } from "../features/invoice/components/InvoiceForm";
import { FinanceRejectInvoiceFormValues } from "../features/invoice/hooks/useFinanceRejectInvoiceHandler";
import { PORejectInvoiceFormValues } from "../features/invoice/hooks/usePORejectInvoiceHandler";
import {
    AmountString,
    BankAccount,
    PaginationResponse,
    StateAndRef,
    UploadedFile,
    UUID,
    WorkflowResult,
} from "../types/common";
import {
    InvoiceState,
    InvoiceStateWithPaymentInfo,
    InvoiceSummary,
    BankAccountDetails,
    InvoiceHistory,
} from "../types/invoice";
import { stringifyAmount } from "../utils/amount";

import request, { ApiError, requestWithToken } from "./common";

const parseToBankAccountDetails = (
    bankAccountList: BankAccount[],
    bankAccountStr: string,
): BankAccountDetails => {
    try {
        const bankAccount = bankAccountList.find(
            (it) => {
                return (it.bankAccountHolderName + "-" + it.bankAccountNumber) === bankAccountStr
            },
        );
        if (!bankAccount) {
            return {
                bankAccountHolderName: "",
                bankAccountNumber: null,
                bankAccountNumberStr: "",
                swiftAddress: "",
                bankName: "",
                registeredAddress: "",
                currency: "",
            };
        }
        return {
            id: bankAccount?.id + "",
            bankAccountHolderName: bankAccount?.bankAccountHolderName,
            bankAccountNumber: null,
            bankAccountNumberStr: bankAccount?.bankAccountNumber,
            swiftAddress: bankAccount?.swiftAddress,
            bankName: bankAccount?.bankName,
            registeredAddress: bankAccount?.registeredAddress,
            currency: bankAccount?.currency,
        };
    } catch (err) {
        console.error(err);
        return {
            bankAccountHolderName: "",
            bankAccountNumber: null,
            bankAccountNumberStr: "",
            swiftAddress: "",
            bankName: "",
            registeredAddress: "",
            currency: "",
        };
    }
};

const invoiceApi = {
    create: (
        claimId: string,
        values: InvoiceFormValues,
        bankAccountList: BankAccount[],
    ): Promise<InvoiceWorkflowResult> => {
        const payload: InvoiceCreationRequest = {
            claimLinearId: claimId,
            amount: stringifyAmount(values),
            documents: values.files.map((it) => ({
                name: it.name,
                id: it.id,
            })),
            bankAccountDetails: parseToBankAccountDetails(bankAccountList, values.bankAccount),
        };
        return request<InvoiceWorkflowResult, InvoiceCreationRequest>(
            "/invoice/submit",
            "POST",
            payload,
        );
    },
    update: (
        ref: string,
        values: InvoiceFormValues,
        bankAccountList: BankAccount[],
    ): Promise<InvoiceWorkflowResult> => {
        const payload: InvoiceUpdateRequest = {
            amount: stringifyAmount(values),
            documents: values.files.map((it) => ({
                name: it.name,
                id: it.id,
            })),
            bankAccountDetails: parseToBankAccountDetails(bankAccountList, values.bankAccount),
        };
        return request<InvoiceWorkflowResult, InvoiceUpdateRequest>(
            `/invoice/update/${ref}`,
            "PUT",
            payload,
        );
    },
    discard: (ref: string): Promise<InvoiceWorkflowResult> => {
        return request<InvoiceWorkflowResult>(
            `/invoice/discard/${ref}`,
            "POST",
        );
    },
    poApprove: (ref: string): Promise<InvoiceWorkflowResult> => {
        return request<InvoiceWorkflowResult>(
            `/invoice/po/approve/${ref}`,
            "POST",
        );
    },
    poReject: (
        ref: string,
        values: PORejectInvoiceFormValues,
    ): Promise<InvoiceWorkflowResult> => {
        const payload: InvoiceRejectRequest = {
            comment: values.comment,
        };
        return request<InvoiceWorkflowResult, InvoiceRejectRequest>(
            `/invoice/po/reject/${ref}`,
            "POST",
            payload,
        );
    },
    financeApprove: (ref: string): Promise<InvoiceWorkflowResult> => {
        return request<InvoiceWorkflowResult>(
            `/invoice/fo/approve/${ref}`,
            "POST",
        );
    },
    financeReject: (
        ref: string,
        values: FinanceRejectInvoiceFormValues,
    ): Promise<InvoiceWorkflowResult> => {
        const payload: InvoiceRejectRequest = {
            comment: values.comment,
        };
        return request<InvoiceWorkflowResult, InvoiceRejectRequest>(
            `/invoice/fo/reject/${ref}`,
            "POST",
            payload,
        );
    },
};

export type useInvoiceApprovedListResponse = PaginationResponse<
    useInvoiceApprovedListResponseData[]
>;
export type useInvoiceApprovedListResponseData = {
    claimId: string;
    mainContractorName: string;
    invoiceId: string;
    paymentOrderId: string;
    projectId: string;
    projectName: string;
    timestamp: string;
};

export const useInvoiceApprovedList = (
    page: number,
    size: number,
    claimId?: string,
    invoiceId?: string,
    mainContractorName?: string,
    paymentOrderId?: string,
    projectId?: string,
    dateFrom?: number | null,
    dateTo?: number | null,
): SWRResponse<useInvoiceApprovedListResponse, ApiError> => {
    const params: string[] = [`page=${page}`, `size=${size}`];

    if (claimId) params.push(`claimId=${claimId}`);
    if (invoiceId) params.push(`invoiceId=${invoiceId}`);
    if (mainContractorName)
        params.push(`mainContractorName=${mainContractorName}`);
    if (paymentOrderId) params.push(`paymentOrderId=${paymentOrderId}`);
    if (projectId) params.push(`projectId=${projectId}`);
    if (dateFrom && dateTo) params.push(`timestamp=${dateFrom}-${dateTo}`);

    const onError = useSWRApiErrorHandler();
    return useSWR<useInvoiceApprovedListResponse, ApiError>(
        `/invoice/list/approved?${params.join("&")}`,
        request,
        { onError },
    );
};

export const useInvoiceList = (
    claimLinearId: UUID,
): SWRResponse<InvoiceStateWithPaymentInfo[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<InvoiceStateWithPaymentInfo[], ApiError>(
        `/invoice/list/${claimLinearId}`,
        request,
        { onError },
    );
};

export const useInvoice = (
    linearId: UUID,
): SWRResponse<StateAndRef<InvoiceState>, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<StateAndRef<InvoiceState>, ApiError>(
        linearId ? `/invoice/linearId/${linearId}/latest` : linearId,
        request,
        { onError },
    );
};

export const useInvoiceWithToken = (
    linearId: UUID,
): SWRResponse<StateAndRef<InvoiceState>, ApiError> => {
    const { token } = useAuth();
    return useSWR<StateAndRef<InvoiceState>, ApiError>(
        token && linearId
            ? `/token-access/invoice/linearId/${linearId}/latest`
            : null,
        requestWithToken(token),
    );
};

export const useInvoiceHistory = (
    linearId: UUID,
): SWRResponse<InvoiceHistory, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<InvoiceHistory, ApiError>(
        `/invoice/history/${linearId}`,
        request,
        { onError },
    );
};

export const useInvoiceSummary = (
    projectLinearId: UUID,
): SWRResponse<InvoiceSummary, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<InvoiceSummary, ApiError>(
        `/invoice/summary/${projectLinearId}`,
        request,
        { onError },
    );
};

type InvoiceCreationRequest = {
    claimLinearId: UUID;
    amount: AmountString;
    documents: UploadedFile[];
    bankAccountDetails?: BankAccountDetails;
};

type InvoiceUpdateRequest = {
    amount: AmountString;
    documents: UploadedFile[];
    bankAccountDetails?: BankAccountDetails;
};

type InvoiceRejectRequest = {
    comment: string;
};


export type InvoiceWorkflowResult = WorkflowResult<{
    InvoiceState: StateAndRef<InvoiceState>[];
}>;

export default invoiceApi;
